import { useCallback, useState } from "react";
import { shouldCelebrate, type AwardedStamp } from "@oshimap/domain";
import { ep } from "../../lib/api";

export interface VisitOutcome {
  ok: boolean;
  awarded: AwardedStamp[];
  message: string;
  /** STAMP 회전각의 고정 시드. 같은 방문은 늘 같은 각으로 찍힌다. */
  visitId?: string;
}

/**
 * FR-PASS-02: 방문 기록은 ⚡STAMP + 📳Heavy 를 발생시킨다.
 * 서버 응답의 awardedStamps[] 가 트리거다 (AC-06 실측).
 * NFR-M-03: Reduced Motion 이면 애니메이션 대신 메시지로 같은 사실을 전한다.
 */
export function useVisit() {
  const [pending, setPending] = useState(false);
  const [outcome, setOutcome] = useState<VisitOutcome | null>(null);

  const record = useCallback(async (
    input: { placeId?: string; eventId?: string; visitedOn: string; memo?: string; rating?: number },
  ): Promise<VisitOutcome> => {
    setPending(true);
    setOutcome(null);
    try {
      const result = await ep.createVisit({ ...input, visibility: "PRIVATE" });
      const awarded = result.awardedStamps;
      const celebrate = shouldCelebrate(result);

      /*
       * 모션은 화면이 그린 뒤에 화면이 건다 (VisitSheet). 여기서 한 번 더 걸면
       * 같은 동작이 두 번 재생되고, 어느 쪽이 진짜인지 알 수 없게 된다.
       */
      const out: VisitOutcome = {
        ok: true,
        awarded,
        visitId: result.visit.id,
        message: celebrate
          ? `${awarded[0]!.name} 스탬프를 받았어요`
          : "방문을 기록했습니다",
      };
      setOutcome(out);
      return out;
    } catch (e) {
      const out: VisitOutcome = {
        ok: false, awarded: [],
        message: e instanceof Error ? e.message : "기록하지 못했어요. 다시 시도해 주세요.",
      };
      setOutcome(out);
      return out;
    } finally {
      setPending(false);
    }
  }, []);

  return { record, pending, outcome, clear: () => setOutcome(null) };
}
